// Role definitions
export type RoleType = 'OWNER' | 'SUPER_ADMIN' | 'FINANCE_ADMIN' | 'ADMIN' | 'SALES_DIRECTOR' | 'AGENT';

export interface Permission {
  // Revenue & Commissions
  viewRevenue: boolean;
  viewGrossCommissions: boolean;
  viewAgencyMetrics: boolean;
  viewDealCosts: boolean;
  viewPayrollData: boolean;
  viewAllCommissions: boolean;
  viewTeamCommissions?: boolean;
  viewOwnCommissions?: boolean;
  viewProjectedCommissions?: boolean;

  // Contacts
  viewAllContacts: boolean;
  viewTeamContacts?: boolean;
  viewAssignedContacts?: boolean;
  assignContacts: boolean;
  deleteContacts: boolean;

  // Policies
  viewAllPolicies: boolean;
  viewTeamPolicies?: boolean;
  viewOwnPolicies?: boolean;
  viewPolicyCosts: boolean;
  editDealTypes: boolean;

  // Analytics
  viewFullAnalytics: boolean;
  viewAgentPerformance: boolean;
  viewTeamPerformance?: boolean;
  viewOwnPerformance?: boolean;
  viewRevenueReports: boolean;
  viewRetentionMetrics: boolean;
  viewTeamRetention?: boolean;

  // Team Management
  viewAllAgents: boolean;
  viewTeamAgents?: boolean;
  manageAgents: boolean;
  assignTeams: boolean;
  manageOwners?: boolean;
  coachTeam?: boolean;

  // Communications
  viewAllCommunications: boolean;
  viewTeamCommunications?: boolean;
  viewOwnCommunications?: boolean;
  createCampaigns: boolean;

  // Settings & Admin
  manageCommissionRates: boolean;
  manageCompanySettings: boolean;
  manageIntegrations: boolean;
  manageRoles: boolean;
  exportAllData: boolean;
  exportTeamData?: boolean;
  manageOwnProfile?: boolean;

  // Appointments
  viewAllAppointments: boolean;
  viewTeamAppointments?: boolean;
  viewOwnAppointments?: boolean;
}

export interface Role {
  name: string;
  level: number;
  permissions: Permission;
}

export interface User {
  id: string;
  email: string;
  name: string;
  firstName: string;
  lastName: string;
  role: RoleType;
  roleName: string;
  roleLevel: number;
  permissions: Permission;
  licensedStates: string[];
  teamMembers?: string[];
  avatarUrl?: string;
  phone?: string;
  npn?: string;
  createdAt: string;
  updatedAt: string;
  mustChangePassword?: boolean;
  capabilities?: Record<string, string>;
}

export interface AuthState {
  user: User | null;
  isAuthenticated: boolean;
  isLoading: boolean;
  error: string | null;
}

// Deal types for commission tracking
export type DealType = 'HEAT' | 'EARN' | 'ADVANCED';

export interface DealTypeConfig {
  name: string;
  description: string;
  keepsPolicies: boolean;
  advancesCommission: boolean;
}

export const DEAL_TYPES: Record<DealType, DealTypeConfig> = {
  HEAT: { name: 'Heat Deal', description: 'Pass-through - agency does not keep policies', keepsPolicies: false, advancesCommission: false },
  EARN: { name: 'Earn Deal', description: 'Standard - agency keeps policies', keepsPolicies: true, advancesCommission: false },
  ADVANCED: { name: 'Advanced Deal', description: 'Commission advanced, pay back events for back-end residuals', keepsPolicies: true, advancesCommission: true }
};

export const ROLES: Record<RoleType, Role> = {
  OWNER: {
    name: 'Agency Owner',
    level: 6,
    permissions: {
      viewRevenue: true, viewGrossCommissions: true, viewAgencyMetrics: true, viewDealCosts: true, viewPayrollData: true, viewAllCommissions: true,
      viewAllContacts: true, assignContacts: true, deleteContacts: true,
      viewAllPolicies: true, viewPolicyCosts: true, editDealTypes: true,
      viewFullAnalytics: true, viewAgentPerformance: true, viewRevenueReports: true, viewRetentionMetrics: true,
      viewAllAgents: true, manageAgents: true, assignTeams: true, manageOwners: true,
      viewAllCommunications: true, createCampaigns: true,
      manageCommissionRates: true, manageCompanySettings: true, manageIntegrations: true, manageRoles: true, exportAllData: true,
      viewAllAppointments: true
    }
  },
  SUPER_ADMIN: {
    name: 'Super Admin',
    level: 5,
    permissions: {
      viewRevenue: true, viewGrossCommissions: true, viewAgencyMetrics: true, viewDealCosts: true, viewPayrollData: true, viewAllCommissions: true,
      viewAllContacts: true, assignContacts: true, deleteContacts: true,
      viewAllPolicies: true, viewPolicyCosts: true, editDealTypes: true,
      viewFullAnalytics: true, viewAgentPerformance: true, viewRevenueReports: true, viewRetentionMetrics: true,
      viewAllAgents: true, manageAgents: true, assignTeams: true, manageOwners: false,
      viewAllCommunications: true, createCampaigns: true,
      manageCommissionRates: true, manageCompanySettings: true, manageIntegrations: true, manageRoles: false, exportAllData: true,
      viewAllAppointments: true
    }
  },
  FINANCE_ADMIN: {
    name: 'Finance Admin',
    level: 4,
    permissions: {
      viewRevenue: true, viewGrossCommissions: true, viewAgencyMetrics: true, viewDealCosts: true, viewPayrollData: true, viewAllCommissions: true,
      viewAllContacts: true, assignContacts: false, deleteContacts: false,
      viewAllPolicies: true, viewPolicyCosts: true, editDealTypes: false,
      viewFullAnalytics: true, viewAgentPerformance: true, viewRevenueReports: true, viewRetentionMetrics: true,
      viewAllAgents: true, manageAgents: false, assignTeams: false,
      viewAllCommunications: true, createCampaigns: false,
      manageCommissionRates: false, manageCompanySettings: false, manageIntegrations: false, manageRoles: false, exportAllData: true,
      viewAllAppointments: true
    }
  },
  ADMIN: {
    name: 'Administrator',
    level: 3,
    permissions: {
      viewRevenue: false, viewGrossCommissions: false, viewAgencyMetrics: true, viewDealCosts: false, viewPayrollData: false, viewAllCommissions: true,
      viewAllContacts: true, assignContacts: true, deleteContacts: false,
      viewAllPolicies: true, viewPolicyCosts: false, editDealTypes: false,
      viewFullAnalytics: false, viewAgentPerformance: true, viewRevenueReports: false, viewRetentionMetrics: true,
      viewAllAgents: true, manageAgents: true, assignTeams: true,
      viewAllCommunications: true, createCampaigns: true,
      manageCommissionRates: false, manageCompanySettings: false, manageIntegrations: false, manageRoles: false, exportAllData: false,
      viewAllAppointments: true
    }
  },
  SALES_DIRECTOR: {
    name: 'Sales Leader',
    level: 2,
    permissions: {
      viewRevenue: false, viewGrossCommissions: false, viewAgencyMetrics: false, viewDealCosts: false, viewPayrollData: false, viewAllCommissions: false,
      viewTeamCommissions: true, viewOwnCommissions: true,
      viewAllContacts: false, viewTeamContacts: true, assignContacts: true, deleteContacts: false,
      viewAllPolicies: false, viewTeamPolicies: true, viewPolicyCosts: false, editDealTypes: false,
      viewFullAnalytics: false, viewAgentPerformance: false, viewTeamPerformance: true, viewRevenueReports: false, viewRetentionMetrics: false, viewTeamRetention: true,
      viewAllAgents: false, viewTeamAgents: true, manageAgents: false, assignTeams: false, coachTeam: true,
      viewAllCommunications: false, viewTeamCommunications: true, createCampaigns: false,
      manageCommissionRates: false, manageCompanySettings: false, manageIntegrations: false, manageRoles: false, exportAllData: false, exportTeamData: true,
      viewAllAppointments: false, viewTeamAppointments: true
    }
  },
  AGENT: {
    name: 'Agent',
    level: 1,
    permissions: {
      viewRevenue: false, viewGrossCommissions: false, viewAgencyMetrics: false, viewDealCosts: false, viewPayrollData: false, viewAllCommissions: false,
      viewTeamCommissions: false, viewOwnCommissions: true, viewProjectedCommissions: true,
      viewAllContacts: false, viewTeamContacts: false, viewAssignedContacts: true, assignContacts: false, deleteContacts: false,
      viewAllPolicies: false, viewTeamPolicies: false, viewOwnPolicies: true, viewPolicyCosts: false, editDealTypes: false,
      viewFullAnalytics: false, viewAgentPerformance: false, viewTeamPerformance: false, viewOwnPerformance: true, viewRevenueReports: false, viewRetentionMetrics: false,
      viewAllAgents: false, viewTeamAgents: false, manageAgents: false, assignTeams: false,
      viewAllCommunications: false, viewTeamCommunications: false, viewOwnCommunications: true, createCampaigns: false,
      manageCommissionRates: false, manageCompanySettings: false, manageIntegrations: false, manageRoles: false, exportAllData: false, manageOwnProfile: true,
      viewAllAppointments: false, viewTeamAppointments: false, viewOwnAppointments: true
    }
  }
};
